import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from 'app/auth';
import {
  isOrganizationManagementAvailable,
  useDeploymentConfig,
} from 'services/config';
import { FEATURE_FLAGS } from '../../consts/features';
import { useSelfServeGate } from '../../hooks/billing/useSelfServeGate';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { usePersonasEnabled } from '../../hooks/usePersonasEnabled';
import { useQodoIndexPermissions } from '../../hooks/useQodoIndexPermissions';
import { useUser } from '../../hooks/useUser';
import { useWikiCapabilityEnabled } from '../../hooks/useWikiCapabilityEnabled';
import { QUERY_KEYS } from '../../network/queries/consts';
import { ROUTE_PATHS, ROUTES } from '../../routes/consts';
import { spawn } from '../../utils/translations';
import { DISABLED_REASON_KEYS, ICONS_MAP, ITEM_ID } from './consts';
import { Section } from './types';

export interface SidebarConfiguration {
  sections: Section[];
  isLoading: boolean;
}

/**
 * Returns the sidebar sections and loading state. Exposed as a hook so that
 * `t()` is called within the React tree — module-scope calls run before i18n
 * is initialized and would return untranslated keys.
 * @returns sections config and the sidebar-skeleton loading state — aggregates most
 *   flag queries; some items (e.g. sandbox) are intentionally excluded and gate their
 *   own visibility while their flag loads.
 */
export const useSidebarConfiguration = (): SidebarConfiguration => {
  const { isOrganizationOwner } = useContext(AuthContext);
  const { t } = useTranslation();
  const deploymentConfig = useDeploymentConfig();
  const organizationManagementAvailable =
    isOrganizationManagementAvailable(deploymentConfig);
  const { isEnterpriseUser, isBillingDashboardEnabled, isTierScopedOwner } =
    useUser();
  const hasQodoIndexPermissions = useQodoIndexPermissions();
  const spawnItemLabel = spawn('sidebar.item');
  const { isEnabled: isBillingEnabled, isLoading: isBillingLoading } =
    useFeatureAccess({ featureId: FEATURE_FLAGS.BILLING });
  const { isEnabled: isReviewConfigEnabled, isLoading: isReviewConfigLoading } =
    useFeatureAccess({ featureId: FEATURE_FLAGS.REVIEW_CONFIGURATIONS });
  const { isEnabled: isFindingsEnabled, isLoading: isFindingsLoading } =
    useFeatureAccess({ featureId: FEATURE_FLAGS.FINDINGS });
  const { isEnabled: isWikiEnabled, isLoading: isWikiLoading } =
    useWikiCapabilityEnabled();
  const queryClient = useQueryClient();
  const {
    isEnabled: isGitRepoManagementEnabled,
    isLoading: isGitRepoManagementLoading,
  } = useFeatureAccess({ featureId: FEATURE_FLAGS.GIT_REPO_MANAGEMENT });

  const {
    isEnabled: isSandboxConfigEnabled,
    isLoading: isSandboxConfigLoading,
  } = useFeatureAccess({ featureId: FEATURE_FLAGS.SANDBOX_CONFIG });

  const { isPersonasEnabled, isPersonasLoading } = usePersonasEnabled();

  const { isSelfServe, isLoading: isSelfServeGateLoading } = useSelfServeGate();

  const onRulesClick = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONFIG] });
  };

  const showBilling = isEnterpriseUser
    ? isOrganizationOwner && isBillingDashboardEnabled
    : isBillingEnabled && isSelfServe;

  const isLoading =
    isGitRepoManagementLoading ||
    isBillingLoading ||
    isSelfServeGateLoading ||
    isFindingsLoading ||
    isPersonasLoading ||
    isWikiLoading;

  return {
    isLoading,
    sections: [
      {
        id: ITEM_ID.PLATFORM,
        items: [
          {
            id: ITEM_ID.HOME,
            title: t(spawnItemLabel(ITEM_ID.HOME)),
            icon: ICONS_MAP[ITEM_ID.HOME],
            href: ROUTES.HOME,
          },
          {
            id: ITEM_ID.MANAGEMENT_REPOSITORIES,
            title: spawnItemLabel(ITEM_ID.MANAGEMENT_REPOSITORIES),
            icon: ICONS_MAP[ITEM_ID.MANAGEMENT_REPOSITORIES],
            href: ROUTES.MANAGEMENT_REPOSITORIES,
            shouldHide:
              !isGitRepoManagementLoading && !isGitRepoManagementEnabled,
          },
          {
            id: ITEM_ID.MONITOR,
            title: spawnItemLabel(ITEM_ID.MONITOR),
            icon: ICONS_MAP[ITEM_ID.MONITOR],
            href: ROUTES.INDEX_MONITOR,
            shouldHide: !hasQodoIndexPermissions,
          },
          {
            id: ITEM_ID.RULES,
            title: t('sidebar.item.review-standards'),
            icon: ICONS_MAP[ITEM_ID.RULES],
            href: ROUTES.RULES,
            onClick: onRulesClick,
            badgeLabel: t('general.beta'),
          },
          {
            id: ITEM_ID.FINDINGS,
            title: t(spawnItemLabel(ITEM_ID.FINDINGS)),
            icon: ICONS_MAP[ITEM_ID.FINDINGS],
            href: ROUTES.FINDINGS,
            shouldHide: !isFindingsLoading && !isFindingsEnabled,
          },
          {
            id: ITEM_ID.WIKI,
            title: t(spawnItemLabel(ITEM_ID.WIKI)),
            icon: ICONS_MAP[ITEM_ID.WIKI],
            href: ROUTES.WIKI,
            shouldHide: !isWikiEnabled,
            badgeLabel: t('general.alpha'),
          },
          {
            id: ITEM_ID.TEAM_PROFILES,
            title: t(spawnItemLabel(ITEM_ID.TEAM_PROFILES)),
            icon: ICONS_MAP[ITEM_ID.TEAM_PROFILES],
            href: ROUTES.TEAM_PROFILES,
            shouldHide: !isPersonasLoading && !isPersonasEnabled,
            badgeLabel: t('general.alpha'),
          },
          {
            id: ITEM_ID.ANALYTICS,
            title: t(spawnItemLabel(ITEM_ID.ANALYTICS)),
            icon: ICONS_MAP[ITEM_ID.ANALYTICS],
            href: ROUTE_PATHS.ANALYTICS,
            disabledReason:
              !isOrganizationOwner &&
              DISABLED_REASON_KEYS.ORGANIZATION_OWNER_ONLY,
            shouldHide: !isEnterpriseUser,
          },
          {
            id: ITEM_ID.CONFIGURATION,
            title: spawnItemLabel(ITEM_ID.CONFIGURATION),
            href: ROUTES.CONFIGURATION,
            icon: ICONS_MAP[ITEM_ID.CONFIGURATION],
            shouldHide: !isReviewConfigLoading && !isReviewConfigEnabled,
          },
          {
            id: ITEM_ID.INTEGRATIONS,
            title: spawnItemLabel(ITEM_ID.INTEGRATIONS),
            icon: ICONS_MAP[ITEM_ID.INTEGRATIONS],
            iconLink: ROUTES.INTEGRATIONS,
            href: ROUTES.INTEGRATIONS,
          },
          {
            id: ITEM_ID.SANDBOX_CONFIG,
            title: t(spawnItemLabel(ITEM_ID.SANDBOX_CONFIG)),
            icon: ICONS_MAP[ITEM_ID.SANDBOX_CONFIG],
            href: ROUTES.SANDBOX_CONFIG,
            // Kept out of the isLoading aggregate (would stall the sidebar for non-sandbox users); hide while its flag loads to avoid a flash.
            shouldHide: isSandboxConfigLoading || !isSandboxConfigEnabled,
          },
          {
            id: ITEM_ID.ACCOUNT,
            title: t(spawnItemLabel(ITEM_ID.ACCOUNT)),
            icon: ICONS_MAP[ITEM_ID.ACCOUNT],
            iconLink: ROUTES.ORGANIZATION,
            shouldHide: !organizationManagementAvailable,
            subItems: [
              {
                id: ITEM_ID.ORGANIZATION,
                title: t(spawnItemLabel(ITEM_ID.ORGANIZATION)),
                href: ROUTES.ORGANIZATION,
              },
              {
                id: ITEM_ID.TEAMS_MANAGEMENT,
                title: t(spawnItemLabel(ITEM_ID.TEAMS_MANAGEMENT)),
                href: ROUTES.TEAMS_MANAGEMENT,
                shouldHide: false,
              },
              {
                id: ITEM_ID.SETTINGS,
                title: t(spawnItemLabel(ITEM_ID.SETTINGS)),
                href: ROUTES.SETTINGS,
              },
              {
                id: ITEM_ID.BILLING,
                title: t(spawnItemLabel(ITEM_ID.BILLING)),
                href: ROUTES.BILLING,
                shouldHide:
                  !isBillingLoading && !isSelfServeGateLoading && !showBilling,
                disabledReason:
                  showBilling &&
                  !isEnterpriseUser &&
                  !isTierScopedOwner &&
                  DISABLED_REASON_KEYS.ORGANIZATION_OWNER_ONLY,
                badgeLabel: t('general.beta'),
              },
            ],
          },
          /*****************************************************
           *** TODO: Uncomment this when the analytics page is ready
           *** https://linear.app/qodo/issue/PLT-1456/create-qm-and-qg-analytics-pages
           *****************************************************/
          // {
          //   id: ITEM_ID.QODO_MERGE_ANALYTICS,
          //   title: spawnItemLabel(ITEM_ID.QODO_MERGE_ANALYTICS),
          //   onClick: () => {
          //     navigate(ROUTES.QODO_MERGE_ANALYTICS);
          //   },
          // },
        ],
      },
    ],
  };
};
